import { Flex, Typography } from 'antd';
import classNames from 'classnames';
import { ReactNode } from 'react';

const { Text } = Typography;
const currencyFormatter = new Intl.NumberFormat('ja-JP');

interface TotalAmountDescription {
  title: string;
  value?: number;
  note?: string;
  unit: string;
}

interface TotalAmountProps {
  title: string;
  totalAmount?: number;
  actions?: ReactNode;
  wrapperClass?: string;
  amountTextSize?: 'default' | 'large';
  wrapperSize?: 'small' | 'middle' | 'large';
  descriptions?: TotalAmountDescription[];
  direction?: 'horizontal' | 'vertical';
}

export function TotalAmount({
  title,
  actions,
  totalAmount,
  wrapperClass,
  descriptions,
  amountTextSize = 'default',
  wrapperSize = 'middle',
  direction = 'vertical',
}: Readonly<TotalAmountProps>) {
  const isHorizontalDirection = direction === 'horizontal';

  return (
    <Flex
      justify="space-between"
      className={classNames('total-amount bg-lightgray', wrapperClass, {
        'p-8': wrapperSize === 'small',
        'p-16': wrapperSize === 'middle',
        'p-24': wrapperSize === 'large',
        'total-amount-with-data': !!descriptions,
      })}
    >
      <Flex
        vertical={!isHorizontalDirection}
        {...(!isHorizontalDirection && !!descriptions && { flex: 1 })}
        {...(isHorizontalDirection && {
          flex: 1,
          align: 'center',
          justify: 'space-between',
        })}
      >
        <Flex vertical>
          {descriptions?.map(({ title, value, note, unit }, index) => {
            const formattedValue = `${
              Number.isInteger(value)
                ? `${currencyFormatter.format(value!)}`
                : '--'
            } ${unit}`;
            return (
              <Flex
                key={title}
                className={classNames({
                  'mb-16':
                    !isHorizontalDirection ||
                    index !== descriptions?.length - 1,
                })}
              >
                <Text
                  className={classNames('description-title', {
                    'description-title-horizontal': isHorizontalDirection,
                  })}
                >
                  {title}
                </Text>
                {note ? (
                  <Flex
                    vertical
                    align="flex-end"
                    className="ml-auto font-semibold"
                  >
                    {formattedValue}
                    <Text className="description-note">{note}</Text>
                  </Flex>
                ) : (
                  <Text className="ml-auto font-semibold">
                    {formattedValue}
                  </Text>
                )}
              </Flex>
            );
          })}
        </Flex>
        <Flex>
          <Text className="total-title">{title}</Text>
          <Text
            className={classNames('total-value', {
              'total-value-min-width': !isHorizontalDirection,
              'total-value-large': amountTextSize === 'large',
            })}
          >
            {Number.isInteger(totalAmount)
              ? currencyFormatter.format(totalAmount!)
              : '--'}{' '}
            円(税抜き)
          </Text>
        </Flex>
      </Flex>
      {actions}
    </Flex>
  );
}
